// App.jsx
import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Route, Routes, useParams, useNavigate, useLocation } from 'react-router-dom';
import BulkInputForm from './components/BulkInputForm.jsx';
import AppraisalResults from './components/AppraisalResults.jsx';
import AppraisalCounter from './components/AppraisalCounter.jsx';
import ComparisonResults from './components/ComparisonResults.jsx';
import ProcessingResults from './components/ProcessingResults.jsx';
import Header from './components/Header.jsx';
import Footer from './components/Footer.jsx';
import { getAppraisal, getComparison, getProcessedAppraisal } from './api.js';
import './App.css';
import { initializeGA, useAnalytics } from './hooks/use-analytics.js';
import { GridLoader } from 'react-spinners';

initializeGA();

const HomePage = () => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  const handleAppraisalComplete = (response) => {
    if (response.comparison_id) {
      navigate(`/c/${response.comparison_id}`);
      return;
    }
    if (response.processed_id) {
      navigate(`/p/${response.processed_id}`);
      return;
    }
    if (response.appraisal_id) {
      navigate(`/a/${response.appraisal_id}`);
      return;
    }
    console.error('No valid ID found in response:', response);
  };

  const handleResults = (response) => {
    if (!response) {
      console.error('No response received');
      return;
    }
    handleAppraisalComplete(response);
  };

  return (
    <>
      <AppraisalCounter />
      <BulkInputForm 
        onResults={handleResults}
        onAppraisalComplete={handleAppraisalComplete}
        trackEvent={trackEvent}
        loading={loading}
        setLoading={setLoading}
      />
      {loading && (
        <div className="loading-screen">
          <GridLoader size={10} color="#f97316" />
          <p className="loading-text">Processing your request...</p>
        </div>
      )}
    </>
  );
};

const AppraisalView = () => {
  const { appraisalId } = useParams();
  const navigate = useNavigate();
  
  const [results, setResults] = useState([]);
  const [comparisonData, setComparisonData] = useState(null);
  const [processingData, setProcessingData] = useState(null);
  const [type, setType] = useState('standard');
  
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const [sellTotal, setSellTotal] = useState(0);
  const [buyTotal, setBuyTotal] = useState(0);
  const [volTotal, setVolTotal] = useState(0);
  const [priceSplit, setPriceSplit] = useState(0);
  const [appraisaDate, setAppraisaDate] = useState(null);
  const [marketHub, setMarketHub] = useState('');
  const [rawInput, setRawInput] = useState('');
  const [corpId, setCorpId] = useState('');
  const [comment, setComment] = useState('');

  // Get the current URL path to determine which API to call
  const location = useLocation();
  const urlType = location.pathname.split('/')[1]; // Gets 'a', 'c', or 'p'

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError('');
        
        // Choose the correct API based on URL
        switch(urlType) {
          case 'p':
            const processedData = await getProcessedAppraisal(appraisalId);
            setType('processed');
            setProcessingData(processedData);
            break;
            
          case 'c':
            const comparisonData = await getComparison(appraisalId);
            setType('comparison');
            setComparisonData({
              ...comparisonData,
              market1: comparisonData.market1 || 'jita',
              market2: comparisonData.market2 || 'ualx'
            });
            break;
            
          case 'a':
          default:
            const appraisalData = await getAppraisal(appraisalId);
            setType('standard');
            setResults(appraisalData.appraisals);
            setSellTotal(appraisalData.total_sell_value);
            setBuyTotal(appraisalData.total_buy_value);
            setVolTotal(appraisalData.total_vol);
            setPriceSplit(appraisalData.price_split);
            setAppraisaDate(appraisalData.created_at);
            setMarketHub(appraisalData.market);
            setRawInput(appraisalData.rawInput);
            setCorpId(appraisalData.corp_id);
            setComment(appraisalData.comment);
        }

      } catch (err) {
        setError('Failed to load. Invalid ID or expired.');
        console.error('Fetch error:', err);
      } finally {
        setLoading(false);
      }
    };

    if (appraisalId) fetchData();
  }, [appraisalId, navigate, urlType]);

  if (error) {
    return <div className="error p-4">{error}</div>;
  }

  return (
    <div className="page-transition">
      {loading ? (
        <div className="loading-screen">
          <GridLoader size={10} color="#f97316" />
          <p className="loading-text">Loading...</p>
        </div>
      ) : type === 'comparison' ? (
        <ComparisonResults 
          results={comparisonData?.appraisals || []}
          market1={comparisonData?.market1}
          market2={comparisonData?.market2}
          sellTotalMarket1={comparisonData?.total_sell_value_market1}
          sellTotalMarket2={comparisonData?.total_sell_value_market2}
          buyTotalMarket1={comparisonData?.total_buy_value_market1}
          buyTotalMarket2={comparisonData?.total_buy_value_market2}
          volTotal={comparisonData?.total_vol}
          appraisaDate={comparisonData?.created_at}
          rawInput={comparisonData?.rawInput}
        />
      ) : type === 'processed' ? (
          <ProcessingResults
            raw_results={processingData.raw_results}
            processed_results={processingData.processed_results}
            leftover_items={processingData.leftover_items}
            total_sell_value_raw={processingData.total_sell_value_raw}
            total_buy_value_raw={processingData.total_buy_value_raw}
            total_vol_raw={processingData.total_vol_raw}
            total_sell_value_processed={processingData.total_sell_value_processed}
            total_buy_value_processed={processingData.total_buy_value_processed}
            total_vol_processed={processingData.total_vol_processed}
            market={processingData.market}
            market_status={processingData.market_status}
            appraisal_id={appraisalId}
            appraisaDate={processingData.created_at}
            yield_percentage={processingData.yield_percentage}
        />
      ) : (
        <AppraisalResults 
          results={results}
          sellTotal={sellTotal}
          buyTotal={buyTotal}
          priceSplit={priceSplit}
          volTotal={volTotal}
          appraisaDate={appraisaDate}
          marketHub={marketHub}
          rawInput={rawInput}
          corpId={corpId}
          comment={comment}
        />
      )}
    </div>
  );
};

function App() {
  return (
    <Router>
      <div className="min-h-screen bg-gray-800 flex flex-col">
        <Header />
        <main className="flex-grow">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/a/:appraisalId" element={<AppraisalView />} />
              <Route path="/c/:appraisalId" element={<AppraisalView />} />
              <Route path="/p/:appraisalId" element={<AppraisalView />} />
            </Routes>
          </div>
        </main>
        <Footer />
      </div>
    </Router>
  );
}

export default App;
